import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import { useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import CountBadge from '@/componentsv2/CountBadge';
import Popover from '@/componentsv2/Popover';

import NotificationRows from './NotificationRows';
import { buildNotificationFeed } from './notificationFeed';
import {
  DISMISS_APP_NOTIFICATION,
  ENGAGEMENT_NOTIFICATIONS,
  ENGAGEMENT_NOTIFICATIONS_UNREAD_COUNT,
  ENGAGEMENT_NOTIFICATION_CONTEXT,
  READ_APP_NOTIFICATIONS,
} from './operations';

/** How often the badge re-counts. Long enough to be free, short enough to notice. */
const UNREAD_POLL_MS = 60_000;

/** One page of rows. The panel scrolls; "load more" is FE6's problem, not FE5's. */
const PAGE_SIZE = 20;

/**
 * The engagement notification bell (ENG-5451 / FE5).
 *
 * Mounted in the app header on every authenticated page, which is why the data
 * is split in two. The badge polls a server-side `COUNT(*)`
 * (`appNotificationsUnreadCount`, ENG-5435) — one small query, no rows. The feed
 * itself is lazy: it is not fetched until the panel is first opened.
 *
 * That split is deliberate and worth preserving. `AccessHeader` is otherwise
 * kept query-free on purpose, and the alternative — fetching the whole feed on
 * every page load just to count unread rows client-side — is what makes an
 * always-mounted bell expensive.
 *
 * Everything the prototype faked is real here: mark-read and dismiss persist
 * server-side, the per-engagement filter is a WHERE clause rather than an
 * in-memory narrowing, and a row's deep link opens the Hub tab the producer
 * named for that event.
 *
 * **Viewer id arrives as a prop, and that is load-bearing.** Reading it from
 * `@/store` would pull the configured store — and with it every domain reducer —
 * into the module graph of the global header, which is exactly what
 * `AccessHeader` avoids by deriving its engagement state from props alone. The
 * mount site already holds the viewer and already evaluates the feature gate, so
 * passing it down costs nothing and keeps the chrome importable without the
 * whole app's Redux graph behind it. The gate lives at the mount for the same
 * reason.
 */
interface NotificationBellLiveProps {
  /** The viewer, for resolving which side of each engagement they are on. */
  viewerUserId: string;
}

export default function NotificationBellLive({
  viewerUserId,
}: NotificationBellLiveProps): JSX.Element {
  const navigate = useNavigate();
  const panelId = useId();

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [engagementId, setEngagementId] = useState<string | null>(null);

  const { data: countData, refetch: refetchCount } = useQuery(
    ENGAGEMENT_NOTIFICATIONS_UNREAD_COUNT,
    { pollInterval: UNREAD_POLL_MS, fetchPolicy: 'cache-and-network' }
  );

  const [loadFeed, feed] = useLazyQuery(ENGAGEMENT_NOTIFICATIONS, {
    fetchPolicy: 'cache-and-network',
  });
  const [loadContext, context] = useLazyQuery(ENGAGEMENT_NOTIFICATION_CONTEXT, {
    fetchPolicy: 'cache-first',
  });

  const [readNotifications] = useMutation(READ_APP_NOTIFICATIONS);
  const [dismissNotification] = useMutation(DISMISS_APP_NOTIFICATION);

  const [actionError, setActionError] = useState<string | null>(null);

  const unread = countData?.appNotificationsUnreadCount ?? 0;

  const view = useMemo(
    () => buildNotificationFeed(feed.data, context.data, viewerUserId),
    [feed.data, context.data, viewerUserId]
  );

  function openPanel() {
    setOpen(true);
    setActionError(null);
    // Filtering re-runs the feed query server-side; the context is per-viewer
    // and cached, so it is fetched once.
    void loadFeed({ variables: { engagementId, limit: PAGE_SIZE, offset: 0 } });
    void loadContext();
  }

  function selectEngagement(next: string | null) {
    setEngagementId(next);
    void loadFeed({ variables: { engagementId: next, limit: PAGE_SIZE, offset: 0 } });
  }

  function refresh() {
    return Promise.all([refetchCount(), feed.refetch?.()]);
  }

  /**
   * Read and dismiss are bookkeeping, and the three mutations are handled the
   * same way for the same reason: a rejected `useMutation` promise must not
   * become an unhandled rejection thrown out of an `onClick`, and must not take
   * the user's actual intent down with it. Failures surface in the panel, the
   * badge is left as it was, and the next poll re-counts.
   */
  function markRead(ids: string[]) {
    if (ids.length === 0) return;
    setActionError(null);
    readNotifications({ variables: { ids } })
      .then(refresh)
      .catch(() => setActionError('Those could not be marked read. Try again in a moment.'));
  }

  function dismiss(id: string) {
    setActionError(null);
    dismissNotification({ variables: { id } })
      .then(refresh)
      .catch(() => setActionError('That could not be dismissed. Try again in a moment.'));
  }

  function activate(id: string, href: string | null) {
    // Navigate first. Marking the row read is bookkeeping and must not gate the
    // click — awaiting it meant a transient failure closed the panel and then
    // went nowhere, turning "Open" into a silent no-op.
    if (href) navigate(href);
    setOpen(false);
    markRead([id]);
  }

  const unreadIds = view.rows.filter((r) => !r.read).map((r) => r.id);

  return (
    <>
      <CountBadge count={unread}>
        <button
          type="button"
          ref={anchorRef}
          onClick={() => (open ? setOpen(false) : openPanel())}
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={open ? panelId : undefined}
          data-testid="engagement-notification-bell"
          className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full text-on-surface hover:bg-surface-secondary"
        >
          <NotificationsNoneOutlined sx={{ fontSize: 24 }} />
        </button>
      </CountBadge>

      <Popover
        id={panelId}
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        ariaLabel="Notifications"
        className="w-[360px] max-w-[calc(100vw-32px)]"
      >
        <NotificationRows
          view={view}
          loading={feed.loading}
          error={!!feed.error}
          engagementId={engagementId}
          unreadIds={unreadIds}
          actionError={actionError}
          onSelectEngagement={selectEngagement}
          onActivate={activate}
          onDismiss={dismiss}
          onMarkVisibleRead={() => markRead(unreadIds)}
          onRetry={() =>
            void loadFeed({ variables: { engagementId, limit: PAGE_SIZE, offset: 0 } })
          }
        />
      </Popover>
    </>
  );
}
